import { Link, useFetcher } from '@remix-run/react';
import { useEffect, type ReactNode } from 'react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { useAuth } from '@modules/root/context';
import { useUserLoggedIn } from '~/modules/user';
import { toast } from 'sonner';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import { useConfigContext } from '@archipro-website/config/bindings/react';
import { RECENT_MAGAZINES_API_ROUTE } from '~/routes/remix-api.recent-magazines';

// Marketing assets — Vite emits hashed URLs for these. They live inside this
// module (per the README boundary) rather than app/page-images/ or app/assets/
// (the latter is gitignored at the repo root).
import imgCol1 from '../images/col-1.png';
import imgCol2 from '../images/col-2.png';
import imgCol3 from '../images/col-3.png';
import imgCatKitchen from '../images/category-kitchen.jpg';
import imgCatBathroom from '../images/category-bathroom.jpg';
import imgCatTilesStone from '../images/category-tiles-stone.jpg';
import imgCatFlooring from '../images/category-flooring.jpg';
import imgCatLighting from '../images/category-lighting.jpg';
import imgCatWindowsDoors from '../images/category-windows-doors.jpg';
import imgCatHeatingCooling from '../images/category-heating-cooling.jpg';
import imgCatFinishes from '../images/category-finishes.jpg';
import imgCatOutdoor from '../images/category-outdoor.jpg';
import imgCatFurniture from '../images/category-furniture.jpg';
import imgCatAppliances from '../images/category-appliances.jpg';
import imgManufacturerBrands from '../images/mfg-1.jpg';
import imgDarkFeature from '../images/dark-feature.jpg';

/**
 * US Products "Coming Soon" landing page.
 *
 * Built for the US launch, where /products ships without the directory. Wired
 * in `app/routes/_app.products.$.tsx` as the swap-in for /products in markets
 * where the products section has not yet launched (US until ops flips
 * `us_product_launch` on); NZ/AU pass through via `countryConfig.allowProducts`
 * and render the existing directory.
 *
 * Ported and matched against the "US PRODUCTS: COMING SOON" Figma frame
 * (node 249:21213; category grid node 249:21240). The desktop (≥1024px / lg)
 * layout reproduces the design's
 * 1920px metrics; below that the page reflows mobile-first — display type
 * scales via clamp(), grids drop columns, and side-by-side blocks stack.
 * Rendered standalone, no props.
 *
 * Deferred (not in this module): the newsletter signup, stats row and footer
 * columns at the foot of the Figma — those belong to the redesigned site footer
 * (FooterV2), whose adoption is a separate follow-up.
 *
 * See `../README.md` for the contract and allowed-edit boundary.
 */

// 12 launch categories from the design, each with a photo from the matching NZ
// directory (sub)category. Hardware has no direct NZ equivalent, so it reuses
// the finishes shot. image is easy to swap per entry.
const CATEGORIES: { name: string; image: string }[] = [
    { name: 'Kitchens', image: imgCatKitchen },
    { name: 'Bathrooms', image: imgCatBathroom },
    { name: 'Tiles & Stone', image: imgCatTilesStone },
    { name: 'Flooring', image: imgCatFlooring },
    { name: 'Lighting', image: imgCatLighting },
    { name: 'Doors & Windows', image: imgCatWindowsDoors },
    { name: 'Heating & Cooling', image: imgCatHeatingCooling },
    { name: 'Hardware', image: imgCatFinishes },
    { name: 'Surfaces & Finishes', image: imgCatFinishes },
    { name: 'Outdoor & Landscape', image: imgCatOutdoor },
    { name: 'Furniture', image: imgCatFurniture },
    { name: 'Appliances', image: imgCatAppliances },
];

// Provenance timeline. Each card is a full-bleed city-skyline photo behind the
// pill + year/caption. `image` holds the CDN object path; the component prefixes
// it with config.assetsBaseUrl (never a hardcoded host) when rendering.
const TIMELINE: {
    pill: string;
    year: string;
    body: ReactNode;
    image: string;
}[] = [
    {
        pill: 'Inception',
        year: '2014',
        body: (
            <>
                Founded in New Zealand.
                <br aria-hidden="true" />
                Built for architecture and design.
            </>
        ),
        image: '/assets/website/ui/images/pr-Inception.png',
    },
    {
        pill: 'Across the Ditch',
        year: '2022',
        body: 'Launched in Australia.',
        image: '/assets/website/ui/images/pr-Across%20the%20Ditch.png',
    },
    {
        pill: 'Stateside',
        year: '2026',
        body: (
            <>
                Stateside Projects, Professionals and Articles.{' '}
                <br aria-hidden="true" />
                Live now.
            </>
        ),
        image: '/assets/website/ui/images/pr-Stateside.png',
    },
    {
        pill: 'Products',
        year: '2026',
        body: (
            <>
                Products, materials and specifications.{' '}
                <br aria-hidden="true" />
                Available Fall 2026.
            </>
        ),
        image: '/assets/website/ui/images/pr-Products.png',
    },
];

// Where the brand-side CTAs ("Apply to Join", "List Your Brand") point. The US
// supplier portal for now — swap when the dedicated signup flow exists.
const BRAND_SIGNUP_URL = 'https://business.archiproamerica.com/get-a-demo';

// ─── Motion ─────────────────────────────────────────────────────────────────
// Restrained, transform/opacity-only motion. All entrances are gated behind
// useReducedMotion() so `prefers-reduced-motion: reduce` users get the content
// instantly with no movement.

const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];

// Container/child variants for staggered grid reveals (category + press cards).
const staggerContainer: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};
const fadeUpItem: Variants = {
    hidden: { opacity: 0, y: '1.5rem' },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE_OUT } },
};

/** Props that fade-and-rise a block as it scrolls into view (once). */
const useScrollReveal = (delay = 0) => {
    const reduce = useReducedMotion();
    if (reduce) return {};
    return {
        initial: { opacity: 0, y: '1.5rem' },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.25 },
        transition: { duration: 0.5, ease: EASE_OUT, delay },
    };
};

const USProductsComingSoonPage = () => {
    const { assetsBaseUrl } = useConfigContext();
    // The parent route (`_app.products.$.tsx`) declares
    // `disableScrollRestoration: true` for the NZ/AU directory's filter UX.
    // The coming-soon page inherits that handle but has no scroll state of
    // its own, so navigating in from a deep-scrolled page (e.g. the footer
    // Products link from a long Projects page) would otherwise land mid-page.
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, []);

    // The design groups sections into "blocks" separated by 3.5rem gaps; within
    // each block the children stack flush (Figma outer flex-col gap).
    // The page is centred and capped at the 120rem (1920px) design width.
    return (
        <div className="mx-auto flex w-full max-w-[120rem] flex-col gap-[2.5rem] overflow-x-clip bg-white pt-[1.5rem] lg:gap-[3.5rem] lg:pt-[7.5rem]">
            <div>
                <PageIdentifier />
                <ImageStrip />
                <LeadParagraph />
            </div>
            <div>
                <DualCTA />
                <CategoriesGrid />
                <DarkFeature />
            </div>
            <PressTimeline />
            {/* Hidden per BC-4457 (kept in place, not deleted, for easy restore). */}
            <div className="hidden">
                <ManufacturerSection
                    eyebrow="(For Brands)"
                    title="Bestselling Brands go Here."
                    image={imgManufacturerBrands}
                    imageOnLeft
                    paddingClass="py-[5rem] lg:pb-0 lg:pt-[12.5rem]"
                />
            </div>
            <ManufacturerSection
                eyebrow="(For Brands)"
                title={
                    <>
                        A platform for the <br aria-hidden="true" />
                        professionally curious.
                    </>
                }
                image={`${assetsBaseUrl}/assets/website/ui/images/mfg-img.png`}
                imageOnLeft={false}
                paddingClass="pb-[5rem] pt-[2.5rem] lg:pb-[12.5rem] lg:pt-[6.0625rem]"
            />
            <StayInTheLoop />
        </div>
    );
};

export default USProductsComingSoonPage;

// ─── Page Identifier ────────────────────────────────────────────────────────

const PageIdentifier = () => {
    const reduce = useReducedMotion();
    // On-load entrance: eyebrow then headline rise in. Skipped for reduced motion.
    const enter = (delay: number) =>
        reduce
            ? {}
            : {
                  initial: { opacity: 0, y: '1.25rem' },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 0.6, ease: EASE_OUT, delay },
              };
    return (
        <section className="relative w-full px-[1.25rem] pt-[2.5rem] md:px-[2.5rem] lg:h-[21.0625rem] lg:px-0 lg:pt-0">
            {/* Desktop: H1 sits at left:486 top:60 w:1298, with a 270px first-line
                text-indent so "(Coming Soon)" can tuck into the gap on the left.
                Mobile: the eyebrow simply stacks above a fluid headline. */}
            <div className="lg:absolute lg:left-[30.375rem] lg:top-[3.75rem] lg:w-[81.125rem]">
                <motion.p
                    className="m-0 mb-[0.75rem] font-sans text-[1.1rem] font-medium leading-[1.03] tracking-[-0.04em] text-[#333] lg:absolute lg:left-0 lg:top-[2.1875rem] lg:mb-0 lg:w-[16.5rem] lg:text-[1.65rem]"
                    {...enter(0.05)}
                >
                    (Coming Soon)
                </motion.p>
                <motion.h1
                    className="m-0 font-display text-[clamp(2.5rem,9vw,7.5rem)] font-medium leading-[0.9] tracking-[-0.02em] text-[#333] lg:text-[7.5rem] lg:[text-indent:16.875rem]"
                    {...enter(0.15)}
                >
                    Products to spec. Materials to source.
                </motion.h1>
            </div>
            <hr className="mx-0 mt-[2rem] h-[0.0625rem] w-full border-0 bg-[#333]/20 lg:absolute lg:bottom-[1.875rem] lg:left-1/2 lg:mt-0 lg:w-[116.875rem] lg:-translate-x-1/2" />
        </section>
    );
};

// ─── Image strip — 4 columns ────────────────────────────────────────────────

// Desktop: the four columns sum wider than the 1920 frame, so the right-most
// column bleeds off the edge (Figma shows only a ~55px sliver) and overflow-hidden
// clips it. Mobile: a horizontal-scroll gallery. col 4 reuses col-2 per Figma.
const STRIP = [
    {
        id: 'col-1',
        src: imgCol1,
        frame: 'h-[18rem] w-[12rem] bg-[#efefef] sm:h-[24rem] sm:w-[16rem] lg:h-[37.5rem] lg:w-[30.0625rem]',
    },
    {
        id: 'col-2',
        src: imgCol2,
        frame: 'h-[24rem] w-[18rem] bg-[#d9d9d9] sm:h-[32rem] sm:w-[24rem] lg:h-[64.5rem] lg:w-[51.75rem]',
    },
    {
        id: 'col-3',
        src: imgCol3,
        frame: 'h-[18rem] w-[12rem] bg-[#fbfbfb] sm:h-[24rem] sm:w-[16rem] lg:h-[37.5rem] lg:w-[30.0625rem]',
    },
    {
        id: 'col-4',
        src: imgCol2,
        frame: 'h-[18rem] w-[12rem] bg-[#d9d9d9] sm:h-[24rem] sm:w-[16rem] lg:h-[37.5rem] lg:w-[30.0625rem]',
    },
];

const ImageStrip = () => {
    const reduce = useReducedMotion();
    return (
        <motion.section
            className="flex w-full items-start gap-[0.75rem] overflow-x-auto px-[1.25rem] lg:gap-[1.5625rem] lg:overflow-hidden lg:px-0"
            variants={reduce ? undefined : staggerContainer}
            initial={reduce ? undefined : 'hidden'}
            animate={reduce ? undefined : 'show'}
        >
            {STRIP.map((col) => (
                <motion.div
                    key={col.id}
                    variants={reduce ? undefined : fadeUpItem}
                    className={`group flex-none overflow-hidden ${col.frame}`}
                >
                    <img
                        src={col.src}
                        alt=""
                        aria-hidden="true"
                        className="motion-safe:duration-[600ms] size-full object-cover motion-safe:transition-transform motion-safe:ease-out motion-safe:group-hover:scale-105"
                    />
                </motion.div>
            ))}
        </motion.section>
    );
};

// ─── Lead paragraph ────────────────────────────────────────────────────────

const LeadParagraph = () => {
    const reveal = useScrollReveal();
    return (
        <section className="px-[1.25rem] py-[4rem] md:px-[2.5rem] lg:px-[1.5625rem] lg:py-[10.625rem]">
            {/* Desktop: "(About)" tucks top-left and the paragraph's first line is
                indented to clear it; mobile stacks the eyebrow above the copy. */}
            <motion.div className="relative" {...reveal}>
                <p className="m-0 mb-[1rem] font-sans text-[1.1rem] font-medium leading-[1.03] tracking-[-0.04em] text-[#333] lg:absolute lg:left-0 lg:top-[1.25rem] lg:mb-0 lg:text-[1.65rem]">
                    (About)
                </p>
                <p className="m-0 w-full max-w-[114.875rem] font-sans text-[clamp(1.75rem,5.5vw,5rem)] font-normal leading-[1.07] tracking-[-0.048em] text-[#181818] lg:[text-indent:12rem]">
                    The products behind the projects are coming to ArchiPro.
                    Soon you&apos;ll be able to explore materials, finishes and
                    furnishings alongside the professionals that use them.
                </p>
            </motion.div>
        </section>
    );
};

// ─── Dual CTA ──────────────────────────────────────────────────────────────

const DualCTA = () => {
    const { showAuthModal } = useAuth();
    const { isLoggedIn } = useUserLoggedIn();
    // No notify backend yet. Guests are sent to sign up (their account is the
    // signal of interest); already-signed-in users get a confirmation toast
    // instead of a pointless login pop-up.
    const handleNotify = () => {
        if (isLoggedIn) {
            toast.success(
                "Thanks — we'll let you know as soon as ArchiPro Products launches in the US.",
            );
            return;
        }
        showAuthModal({
            authSource: 'topNav',
            subtitle:
                'Sign up to be first to know when ArchiPro Products launches in the US.',
        });
    };
    return (
        <section className="border-t-[0.0625rem] border-t-[#e5e5e5] bg-white px-[1.25rem] py-[2.5rem] md:px-[2.5rem] lg:px-[1.5625rem] lg:py-[3.75rem]">
            <div className="grid grid-cols-1 gap-[0.75rem] lg:grid-cols-2 lg:gap-[1rem]">
                <CTAColumn
                    label="FOR THE BUYER"
                    title="Get early access."
                    body="Homeowners, architects and designers can be among the first to explore products when they arrive on ArchiPro."
                    buttonLabel="Notify me"
                    onButtonClick={handleNotify}
                />
                <CTAColumn
                    label="FOR THE BRAND"
                    title="Bring your brand to ArchiPro."
                    body="Manufacturers, suppliers and distributors can register their interest in joining the products platform ahead of launch."
                    buttonLabel="Apply to Join"
                    buttonHref={BRAND_SIGNUP_URL}
                />
            </div>
        </section>
    );
};

interface CTAColumnProps {
    label: string;
    title: string;
    body: ReactNode;
    buttonLabel: string;
    /** Link target. Omit when using onButtonClick. */
    buttonHref?: string;
    /** Click handler (e.g. open a modal) — renders a <button> instead of a link. */
    onButtonClick?: () => void;
    className?: string;
}

const CTAColumn = ({
    label,
    title,
    body,
    buttonLabel,
    buttonHref,
    onButtonClick,
    className = '',
}: CTAColumnProps) => {
    const reveal = useScrollReveal();
    const isExternal = !!buttonHref && /^https?:\/\//.test(buttonHref);
    const buttonClass =
        'flex h-[3rem] w-fit items-center justify-center rounded-full bg-[#323232] px-[1.75rem] font-sans text-[0.875rem] font-medium uppercase tracking-[0.04em] text-[#f2f2f2] transition-[background-color,transform] duration-200 hover:bg-[#454545] motion-safe:hover:-translate-y-[0.125rem]';
    return (
        <div
            className={`flex min-h-[20rem] flex-col justify-between bg-[#f2f2f2] px-[1.25rem] py-[2.5rem] lg:min-h-[30rem] lg:px-[1.85rem] lg:py-[5.375rem] ${className}`}
        >
            <p className="m-0 font-sans text-[0.8125rem] font-medium uppercase tracking-[0.08em] text-[#323232] md:pb-[3rem]">
                {label}
            </p>
            <motion.div
                className="flex w-full max-w-[55rem] flex-col gap-[1.25rem] p-[0.5rem] md:p-0 lg:gap-[3rem]"
                {...reveal}
            >
                <h2 className="m-0 text-[2.5rem] leading-[1.03] tracking-[-0.048em] text-[#323232] lg:text-[4.375rem]">
                    {title}
                </h2>
                <p className="m-0 w-full max-w-[47.5rem] text-[1.125rem] font-medium leading-[1.1] tracking-[-0.03em] text-[#555] lg:text-[1.625rem]">
                    {body}
                </p>
                {onButtonClick ? (
                    <button
                        type="button"
                        onClick={onButtonClick}
                        className={buttonClass}
                    >
                        {buttonLabel}
                    </button>
                ) : isExternal ? (
                    <a
                        href={buttonHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={buttonClass}
                    >
                        {buttonLabel}
                    </a>
                ) : (
                    <Link to={buttonHref ?? '#'} className={buttonClass}>
                        {buttonLabel}
                    </Link>
                )}
            </motion.div>
        </div>
    );
};

// ─── Categories grid — 12 numbered cards ───────────────────────────────────

const CategoriesGrid = () => {
    const reduce = useReducedMotion();
    const reveal = useScrollReveal();
    return (
        <section className="px-5 pb-20 pt-10 md:px-10 lg:px-[5rem] lg:pb-[12.5rem] lg:pt-[3.75rem]">
            <div className="mb-8 flex items-end justify-between lg:mb-[3.75rem]">
                <motion.h2
                    className="m-0 max-w-[81.25rem] font-sans text-[clamp(2.25rem,6vw,6.25rem)] font-medium leading-none tracking-[-0.03em] text-[#323232]"
                    {...reveal}
                >
                    Every category,
                    <br aria-hidden="true" />
                    from studs to swatches.
                </motion.h2>
            </div>
            <motion.div
                className="grid grid-cols-2 gap-x-3 gap-y-8 md:grid-cols-3 lg:grid-cols-4 lg:gap-x-[1rem] lg:gap-y-[3.75rem]"
                variants={reduce ? undefined : staggerContainer}
                initial={reduce ? undefined : 'hidden'}
                whileInView={reduce ? undefined : 'show'}
                viewport={{ once: true, amount: 0.15 }}
            >
                {CATEGORIES.map((cat, i) => (
                    <motion.article
                        key={cat.name}
                        variants={reduce ? undefined : fadeUpItem}
                        className="group relative flex h-[13rem] flex-col justify-between overflow-hidden p-4 sm:h-[16rem] lg:h-[26.25rem] lg:p-[1.5rem]"
                    >
                        {/* Full-bleed category photo + a top-and-bottom scrim so
                            the white numerals and label stay legible over any
                            image; the photo zooms gently on hover. */}
                        <img
                            src={cat.image}
                            alt=""
                            aria-hidden="true"
                            className="motion-safe:duration-[600ms] absolute inset-0 size-full object-cover motion-safe:transition-transform motion-safe:ease-out motion-safe:group-hover:scale-105"
                        />
                        <div
                            className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30"
                            aria-hidden="true"
                        />
                        <header className="relative flex items-start justify-between font-sans text-[0.8125rem] tracking-[0.08em] text-white">
                            <span>({String(i + 1).padStart(2, '0')})</span>
                            <span className="uppercase text-white/70">
                                — Soon
                            </span>
                        </header>
                        <h3 className="relative m-0 w-full font-sans text-[clamp(1.25rem,3.5vw,2.25rem)] font-medium leading-none tracking-[-0.01em] text-white">
                            {cat.name}
                        </h3>
                    </motion.article>
                ))}
            </motion.div>
        </section>
    );
};

// ─── Dark feature ──────────────────────────────────────────────────────────

const DarkFeature = () => {
    const reduce = useReducedMotion();
    // Opacity-only reveal: these elements are positioned with Tailwind translate
    // utilities, so animating `y` here would clobber their transform.
    const fade = reduce
        ? {}
        : {
              initial: { opacity: 0 },
              whileInView: { opacity: 1 },
              viewport: { once: true, amount: 0.3 },
          };
    return (
        <section className="relative w-full overflow-hidden bg-[#1a1a1a] px-[1.25rem] py-[5rem] md:px-[2.5rem] lg:h-[66.9375rem] lg:px-0 lg:py-0">
            {/* Full-bleed feature photo (Figma node 371:33411) + a 10% black
                scrim for legibility. A very slow Ken-Burns scale adds depth;
                bg-[#1a1a1a] is the fallback while the image loads. */}
            <motion.img
                src={imgDarkFeature}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 size-full origin-center object-cover"
                animate={reduce ? undefined : { scale: [1, 1.06] }}
                transition={
                    reduce
                        ? undefined
                        : {
                              duration: 16,
                              repeat: Infinity,
                              repeatType: 'reverse',
                              ease: 'easeInOut',
                          }
                }
            />
            <div className="absolute inset-0 bg-black/10" aria-hidden="true" />
            {/* lg:contents drops this wrapper on desktop so the children position
                absolutely against the section; on mobile they stack in flow. */}
            <div className="relative flex flex-col gap-[2.5rem] lg:contents">
                <motion.h2
                    className="m-0 text-center font-ghost text-[clamp(2rem,6vw,4.8625rem)] font-normal leading-[0.94] tracking-[-0.02em] text-[#f9f9f7] lg:absolute lg:left-1/2 lg:top-1/2 lg:w-[110rem] lg:max-w-[calc(100%-10rem)] lg:-translate-x-1/2 lg:-translate-y-1/2"
                    {...fade}
                    transition={{ duration: 0.7, ease: EASE_OUT }}
                >
                    The products behind good projects.
                </motion.h2>
                <motion.p
                    className="m-0 font-sans text-[clamp(1rem,1.6vw,1.25rem)] font-medium leading-[1.4] tracking-[-0.02em] text-white lg:absolute lg:bottom-[10.4375rem] lg:left-[5.9375rem] lg:w-[26rem]"
                    {...fade}
                    transition={{ duration: 0.7, ease: EASE_OUT, delay: 0.15 }}
                >
                    Homeowners, architects and designers will soon be able to
                    discover the products, materials and finishes behind the
                    work they admire. For brands, it&apos;s an opportunity to be
                    part of those conversations.
                </motion.p>
            </div>
        </section>
    );
};

// ─── Press timeline ────────────────────────────────────────────────────────

const PressTimeline = () => {
    const reduce = useReducedMotion();
    const reveal = useScrollReveal();
    // Skyline assets live in the CDN bucket; build their URLs from the config's
    // assetsBaseUrl rather than hardcoding a host (the sanctioned pattern).
    const { assetsBaseUrl } = useConfigContext();
    return (
        <section className="bg-white px-[1.25rem] pb-[3rem] pt-[5rem] md:px-[2.5rem] lg:px-[5rem] lg:pb-[6.25rem] lg:pt-[12.5rem]">
            <div className="mb-[2rem] flex w-full items-center justify-center overflow-clip lg:mb-[3.75rem]">
                <motion.h2
                    className="m-0 max-w-[81.0121875rem] break-words text-center font-sans text-[clamp(2rem,5.5vw,5rem)] font-normal leading-[1.1] text-[#181818] lg:text-[5rem] lg:tracking-[-0.24rem]"
                    {...reveal}
                >
                    Built in New Zealand. Proven in Australia. Coming to
                    America.
                </motion.h2>
            </div>
            <motion.div
                className="mx-auto grid w-full max-w-[110rem] grid-cols-1 gap-[0.75rem] sm:grid-cols-2 lg:grid-cols-4 lg:gap-[1rem]"
                variants={reduce ? undefined : staggerContainer}
                initial={reduce ? undefined : 'hidden'}
                whileInView={reduce ? undefined : 'show'}
                viewport={{ once: true, amount: 0.2 }}
            >
                {TIMELINE.map((row) => (
                    <motion.article
                        key={row.pill}
                        variants={reduce ? undefined : fadeUpItem}
                        className="group relative flex h-[20rem] w-full flex-col items-start justify-between overflow-hidden bg-[#323232] px-[1.5rem] pb-[1.5rem] pt-[1.25rem] lg:h-[33.75rem] lg:w-[26.9375rem] lg:max-w-full lg:justify-self-center"
                    >
                        <img
                            src={`${assetsBaseUrl}${row.image}`}
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            className="motion-safe:duration-[600ms] pointer-events-none absolute inset-0 size-full object-cover motion-safe:transition-transform motion-safe:ease-out motion-safe:group-hover:scale-105"
                        />
                        {/* Bottom scrim keeps white year/caption legible while photos load,
                            fail, or have bright sky at the lower edge. */}
                        <div
                            className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent"
                            aria-hidden="true"
                        />
                        <span className="relative shrink-0 overflow-clip bg-white px-[1rem] py-[0.5rem] font-sans text-[0.75rem] font-medium uppercase leading-normal tracking-[0.075rem] text-[#323232]">
                            {row.pill}
                        </span>
                        <p className="relative m-0 max-w-[23.9375rem] shrink-0 whitespace-pre-wrap break-words font-sans text-[1rem] font-medium leading-[1.4] tracking-[-0.01rem] text-white">
                            {row.year}
                            <br aria-hidden="true" />
                            {row.body}
                        </p>
                    </motion.article>
                ))}
            </motion.div>
        </section>
    );
};

// ─── Manufacturer sections ──────────────────────────────────────────────────

interface ManufacturerSectionProps {
    eyebrow: string;
    title: ReactNode;
    image: string;
    /** Image left + text right (Brands), or text left + image right (Platform). */
    imageOnLeft: boolean;
    /** Vertical padding differs between the two instances per Figma. */
    paddingClass: string;
}

const ManufacturerSection = ({
    eyebrow,
    title,
    image,
    imageOnLeft,
    paddingClass,
}: ManufacturerSectionProps) => {
    const reduce = useReducedMotion();
    const reveal = useScrollReveal();
    const isPlatform = !imageOnLeft;
    // DOM order is always text-then-image so the headline leads on mobile; on
    // desktop the "Brands" variant flips to image-left via order utilities.
    const imageBlock = (
        <motion.div
            className={`group w-full lg:flex-none ${imageOnLeft ? 'lg:order-1 lg:w-[45rem]' : 'lg:w-[45rem]'}`}
            {...(reduce ? {} : reveal)}
        >
            <div className="h-[20rem] w-full overflow-hidden bg-[#f2f2f2] sm:h-[28rem] lg:h-[55rem] lg:w-[45rem]">
                <img
                    src={image}
                    alt=""
                    aria-hidden="true"
                    className={`motion-safe:duration-[600ms] size-full object-cover motion-safe:transition-transform motion-safe:ease-out motion-safe:group-hover:scale-105 ${isPlatform ? 'object-bottom' : ''}`}
                />
            </div>
        </motion.div>
    );

    const textBlock = (
        <motion.div
            className={`flex w-full flex-col lg:w-[60rem] lg:flex-none lg:pt-[3.75rem] ${imageOnLeft ? 'gap-[1.5rem] lg:order-2 lg:gap-[2.5rem]' : 'gap-[2.5rem]'}`}
            {...(reduce ? {} : reveal)}
        >
            <p
                className={`m-0 font-sans font-medium text-[#323232] ${isPlatform ? 'text-[0.875rem] tracking-[0.035rem]' : 'text-[0.875rem] tracking-[0.04em]'}`}
            >
                {eyebrow}
            </p>
            <h2
                className={`m-0 w-full break-words font-sans font-normal leading-[1.1] text-[#181818] ${isPlatform ? 'text-[clamp(2.75rem,6vw,5rem)] lg:max-w-[60rem] lg:text-[5rem] lg:tracking-[-0.24rem]' : 'text-[2.75rem] tracking-[-0.048em] lg:w-[60rem] lg:text-[5rem]'}`}
            >
                {title}
            </h2>
            <div className="flex w-full flex-col gap-[1.5rem] lg:w-[45rem]">
                <p
                    className={`m-0 font-sans text-[1.375rem] font-medium leading-[1.4] text-[#323232] ${isPlatform ? 'tracking-[-0.01375rem]' : 'tracking-[-0.01em]'}`}
                >
                    Whether you&apos;re specifying for a client or planning your
                    own home, ArchiPro Products is designed to make product
                    discovery easier. Compare products, explore variations,
                    access technical documentation and keep track of products
                    worth considering as your project takes shape.
                </p>
                <a
                    href={BRAND_SIGNUP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex h-[2.625rem] w-fit min-w-[10.625rem] items-center justify-center gap-[0.5rem] bg-[#212121] px-[1.5rem] py-[0.5rem] font-sans text-[1rem] font-medium leading-[1.25rem] text-[#f2f2f2] transition-[background-color,transform] duration-200 hover:bg-[#3a3a3a] motion-safe:hover:-translate-y-[0.125rem] ${isPlatform ? 'rounded-[1.875rem]' : 'rounded-full tracking-[0]'}`}
                >
                    List Your Brand
                </a>
            </div>
        </motion.div>
    );

    return (
        <section
            className={`flex flex-col gap-[2rem] bg-white px-[1.25rem] md:px-[2.5rem] lg:flex-row lg:items-start lg:gap-[5rem] lg:px-[5rem] ${paddingClass}`}
        >
            {textBlock}
            {imageBlock}
        </section>
    );
};

// ─── Stay in the loop ───────────────────────────────────────────────────────

const NEWSLETTER_TAGS = [
    'Curated Collection',
    'The Commercial Edit',
    'The Project Portfolio',
    'Build',
];

// Magazine cover shape returned by /remix-api/recent-magazines (FooterMagazineTile).
interface MagazineTile {
    id: number;
    title: string;
    imageSrc: string;
    link: string;
}

// Skeleton keys shown while the magazine covers load.
const MAGAZINE_SKELETONS = ['01', '02', '03', '04'];

const StayInTheLoop = () => {
    const reveal = useScrollReveal();
    // Latest digital magazines, fetched client-side (mirrors the EditorsChoice
    // section) so the shared /products loader stays untouched.
    const magazinesFetcher = useFetcher<{ magazines: MagazineTile[] }>();
    useEffect(() => {
        magazinesFetcher.load(RECENT_MAGAZINES_API_ROUTE);
        // fetcher from useFetcher() is a stable reference — load once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const magazines = magazinesFetcher.data?.magazines ?? [];
    const magazinesLoading = magazinesFetcher.state === 'loading';
    return (
        <section className="flex flex-col items-stretch bg-white px-[1.25rem] pb-[5rem] pt-[2.5rem] lg:gap-[8.125rem] lg:px-[1.5625rem] lg:pb-[12.5rem] lg:pt-[6.25rem]">
            <div className="relative w-full lg:min-h-[31.625rem]">
                <div className="flex w-full flex-col gap-[1.0625rem]">
                    <hr className="m-0 h-[0.0625rem] w-full border-0 bg-[#e5e5e5]" />
                    <motion.p
                        className="m-0 break-words font-sans text-[clamp(1.5rem,4vw,2rem)] font-normal leading-[2.5rem] tracking-[-0.02rem] text-[#1f1f21]"
                        {...reveal}
                    >
                        (Articles)
                    </motion.p>
                </div>
                <motion.h2
                    className="m-0 mt-[2.5rem] break-words font-sans text-[clamp(3rem,14vw,12.5rem)] font-normal leading-[0.9] text-[#1f1f21] lg:absolute lg:left-0 lg:top-[9.3075rem] lg:mt-0 lg:leading-[11.25rem] lg:tracking-[-0.525rem]"
                    {...reveal}
                >
                    Stay in the loop
                </motion.h2>
            </div>
            <div className="flex w-full max-w-[116.875rem] flex-col gap-[2.5rem] lg:flex-row lg:items-start lg:justify-between lg:px-12">
                <div className="flex w-full flex-col gap-[1.3125rem] lg:w-[29.1875rem] lg:flex-none">
                    <p className="m-0 font-sans text-[1.25rem] font-normal leading-none text-[#7f7f7f] opacity-60">
                        Digital Newsletters
                    </p>
                    <ul className="m-0 flex flex-wrap content-center gap-[0.375rem] p-0">
                        {NEWSLETTER_TAGS.map((tag) => (
                            <li
                                key={tag}
                                className="list-none rounded-full border border-solid border-black px-[0.875rem] font-sans text-[0.75rem] font-normal uppercase leading-[1.875rem] text-[#1f1f21]"
                            >
                                {tag}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="flex w-full flex-col gap-[4.1875rem] lg:w-[52.75rem] lg:flex-none">
                    <p className="m-0 max-w-[48.46425rem] break-words font-sans text-[clamp(1.5rem,3.5vw,2.4375rem)] font-normal leading-[1.3] text-[#1f1f21]">
                        Be the first to hear about new products, platform
                        updates and what&apos;s coming next on ArchiPro.
                    </p>
                    <NewsletterSubscribe />
                </div>
            </div>
            {/* Latest digital magazines — draggable carousel (embla: drag + wheel)
                so every fetched cover is reachable; the last card bleeds off the
                right edge. Skeletons while loading; nothing when empty. */}
            {magazines.length > 0 ? (
                <motion.div
                    {...reveal}
                    className="w-full self-stretch pt-[2.5rem] md:pt-[5rem]"
                >
                    <Carousel
                        options={{
                            align: 'start',
                            dragFree: true,
                            containScroll: 'trimSnaps',
                        }}
                    >
                        <CarouselContent className="gap-[0.75rem] lg:gap-[1rem]">
                            {magazines.map((mag) => (
                                <CarouselItem
                                    key={mag.id}
                                    className="basis-[15rem] sm:basis-[18rem] lg:basis-[31.67581rem]"
                                >
                                    <Link
                                        to={`${mag.link}?from=products`}
                                        prefetch="intent"
                                        className="group block aspect-[3/5] w-full overflow-hidden rounded-[0.31056rem] bg-[#1a1a1a]"
                                    >
                                        {mag.imageSrc ? (
                                            <img
                                                src={mag.imageSrc}
                                                alt={mag.title}
                                                loading="lazy"
                                                className="motion-safe:duration-[600ms] size-full object-cover motion-safe:transition-transform motion-safe:ease-out motion-safe:group-hover:scale-105"
                                            />
                                        ) : (
                                            <span className="flex size-full items-center justify-center p-[1.25rem] text-center font-sans text-[1.5rem] font-medium leading-[1.05] text-white">
                                                {mag.title}
                                            </span>
                                        )}
                                    </Link>
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                    </Carousel>
                </motion.div>
            ) : magazinesLoading ? (
                <div className="flex w-full gap-[0.75rem] overflow-hidden lg:gap-[1rem]">
                    {MAGAZINE_SKELETONS.map((n) => (
                        <div
                            key={n}
                            aria-hidden="true"
                            className="aspect-[3/5] w-[15rem] flex-none animate-pulse rounded-[0.31056rem] bg-[#ebebeb] sm:w-[18rem] lg:w-[31.67581rem]"
                        />
                    ))}
                </div>
            ) : null}
        </section>
    );
};

// Email capture for "Stay in the loop". Custom pill to match the Figma (light
// field + dark "Subscribe" button), wired to the site's existing newsletter
// endpoint (the same /remix-api/user/subscription action the footer posts to).
const SUBSCRIPTION_ACTION = '/remix-api/user/subscription';

const NewsletterSubscribe = () => {
    const fetcher = useFetcher<{ message?: string; success?: boolean }>();
    const success = !!fetcher.data?.success;
    const isSubmitting =
        fetcher.state === 'submitting' || fetcher.state === 'loading';
    const error =
        fetcher.state === 'idle' && fetcher.data && !success
            ? fetcher.data.message
            : undefined;

    if (success) {
        return (
            <p
                className="m-0 font-sans text-[1.0625rem] font-medium text-[#323232]"
                role="status"
                aria-live="polite"
            >
                Thanks — you&apos;re subscribed.
            </p>
        );
    }

    return (
        <fetcher.Form method="post" action={SUBSCRIPTION_ACTION}>
            <div className="relative flex h-[2.75rem] w-full max-w-[38.5rem] items-center rounded-full bg-[#f6f6f6] pl-[1rem] lg:h-[4.5rem] lg:pl-[2.1875rem]">
                <input
                    type="email"
                    name="email"
                    required
                    disabled={isSubmitting}
                    placeholder="Email Address"
                    aria-label="Email address"
                    autoComplete="email"
                    className="min-w-0 flex-1 bg-transparent pr-[7.5rem] font-sans text-[0.875rem] text-[#181818] placeholder:font-normal placeholder:text-[#181818] placeholder:opacity-30 focus:outline-none disabled:opacity-60 lg:pr-[15.5rem] lg:text-[1.41875rem] lg:tracking-[-0.0425625rem] lg:placeholder:text-[1.41875rem]"
                />
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="absolute right-[0.25rem] top-[0.25rem] flex h-[2.25rem] shrink-0 items-center justify-center gap-[0.25rem] rounded-full bg-[#181818] px-[0.875rem] font-sans text-[0.75rem] font-normal text-white shadow-[0_0.2026875rem_0.7094375rem_rgba(0,0,0,0.11)] transition-[background-color,transform] duration-200 hover:bg-[#323232] disabled:opacity-60 motion-safe:hover:-translate-y-[0.125rem] lg:right-[0.375625rem] lg:top-[0.37rem] lg:h-[3.75rem] lg:min-w-[14.391875rem] lg:gap-[0.5rem] lg:px-[1.723rem] lg:py-[0.7094375rem] lg:pr-[1.098125rem] lg:text-[1.41875rem] lg:tracking-[-0.0425625rem]"
                >
                    {isSubmitting ? 'Subscribing…' : 'Subscribe'}
                    <span aria-hidden="true">→</span>
                </button>
            </div>
            {error ? (
                <p className="m-0 mt-[0.5rem] font-sans text-[0.875rem] text-[#c0392b]">
                    {error}
                </p>
            ) : null}
        </fetcher.Form>
    );
};
